import { useEffect, useState } from 'react'
import { ExternalLink, Heart, Search, User } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import { ToastProvider, apiGet } from '../admin/primitives.js'
import { Trail } from '../navigation/Trail.js'
import { InstagramDataProvider } from './InstagramData.js'
import {
  IgAudienceWidget, IgDemographicsWidget, IgProfileWidget, IgReachViewsWidget,
} from './InstagramWidgets.js'
import { IgContentListPage } from './InstagramContentPages.js'
import { estErreurApi, fraicheurFr, nombreFr } from './lib.js'

/**
 * COMMUNICATION V2 — les FONCTIONS du module Instagram (§42).
 *
 * Publications / Reels / Stories réutilisent la médiathèque existante avec
 * le type VERROUILLÉ (mêmes données, mêmes routes API — aucune deuxième
 * implémentation, §43). Commentaires = le fil global du compte. Audience =
 * les widgets réels d'audience composés en page fixe.
 */

export function IgPublicationsPage() {
  return <IgContentListPage kindLock="FEED" titre="Publications"
    sousTitre="Les posts et carrousels du feed — métriques réelles, médias archivés." />
}

export function IgReelsPage() {
  return <IgContentListPage kindLock="REEL" titre="Reels"
    sousTitre="Les Reels du compte et leur performance réelle." />
}

export function IgStoriesPage() {
  return <IgContentListPage kindLock="STORY" titre="Stories"
    sousTitre="Les Stories archivées par Gestion pendant leurs 24 h de vie — une Story jamais capturée reste honnêtement absente." />
}

/* ═══════════ AUDIENCE — les widgets réels, composés (§49) ═══════════ */

export function IgAudiencePage() {
  const meta = useShellMeta()
  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <Trail />
        <header className="cm-head">
          <div>
            <h1 className="lc-title">Audience</h1>
            <p className="lc-subtitle">Abonnés, portée, démographie et activité du profil — données Meta, relevées par Gestion.</p>
          </div>
        </header>
        <ToastProvider>
          <InstagramDataProvider>
            <div className="igm-audience lc-stagger">
              <div className="igm-audience__large"><IgAudienceWidget /></div>
              <IgReachViewsWidget />
              <IgProfileWidget />
              <IgDemographicsWidget />
            </div>
          </InstagramDataProvider>
        </ToastProvider>
      </main>
    </Shell>
  )
}

/* ═══════════ COMMENTAIRES — le fil global du compte (§48) ═══════════ */

interface CommentGlobal {
  id: string
  author_username: string | null
  text: string
  like_count: number | null
  provider_timestamp: string | null
  missing_since: string | null
  parent_comment_id: string | null
  content_id: string | null
  content_kind: string | null
}

const KIND_COURT: Record<string, string> = {
  REEL: 'Reel', STORY: 'Story', CAROUSEL_ALBUM: 'Carrousel', IMAGE: 'Post', VIDEO: 'Vidéo',
}

export function IgCommentairesPage() {
  const meta = useShellMeta()
  const [items, setItems] = useState<CommentGlobal[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [recherche, setRecherche] = useState('')
  const [rechercheActive, setRechercheActive] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => { setPage(0); setItems([]) }, [rechercheActive])
  useEffect(() => {
    const qp = new URLSearchParams({ page: String(page) })
    if (rechercheActive) qp.set('search', rechercheActive)
    void apiGet<{ items: CommentGlobal[]; total: number } | { error: string; message?: string }>(
      `/api/v1/communication/instagram/commentaires?${qp}`)
      .then((j) => {
        if (estErreurApi(j)) { setErreur(j.message ?? 'Lecture impossible.'); return }
        setErreur(null); setTotal(j.total)
        setItems((prev) => (page === 0 ? j.items : [...prev, ...j.items]))
      })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [page, rechercheActive])

  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <Trail />
        <header className="cm-head">
          <div>
            <h1 className="lc-title">Commentaires</h1>
            <p className="lc-subtitle">
              Tous les commentaires reçus, tous contenus confondus —
              {total > 0 ? ` ${nombreFr(total)} synchronisé${total > 1 ? 's' : ''}.` : ' selon ce que Meta expose.'}
            </p>
          </div>
        </header>
        {erreur ? <p className="lc-hint">{erreur}</p> : null}

        <div className="igc-recherche lc-in">
          <Search size={14} aria-hidden="true" />
          <input className="lc-input" value={recherche} placeholder="Rechercher un commentaire ou un auteur…"
            onChange={(e) => setRecherche(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setRechercheActive(recherche.trim()) }}
            aria-label="Rechercher dans les commentaires" />
        </div>

        {items.length === 0 && !erreur ? (
          <p className="lc-hint">
            {rechercheActive ? 'Aucun commentaire ne correspond.'
              : 'Aucun commentaire synchronisé pour l’instant — Meta peut filtrer certains commentaires (comptes privés).'}
          </p>
        ) : null}

        <div className="igc-liste lc-stagger">
          {items.map((c) => (
            <div key={c.id} className={`igc-item lc-in ${c.missing_since ? 'igc-item--disparu' : ''}`}>
              {c.author_username
                ? <span className="igc-avatar" aria-hidden="true">{c.author_username.slice(0, 1).toUpperCase()}</span>
                : <span className="igc-avatar" aria-hidden="true"><User size={14} /></span>}
              <div className="igc-corps">
                <div className="igc-tete">
                  {c.author_username ? (
                    <a href={`https://www.instagram.com/${encodeURIComponent(c.author_username)}/`}
                      target="_blank" rel="noreferrer" className="igc-user">
                      @{c.author_username} <ExternalLink size={11} aria-hidden="true" />
                    </a>
                  ) : <span className="igc-user igc-user--anon">Compte inconnu</span>}
                  <span className="igc-quand">{c.provider_timestamp ? fraicheurFr(c.provider_timestamp) : ''}</span>
                  {typeof c.like_count === 'number' && c.like_count > 0
                    ? <span className="igc-likes"><Heart size={11} aria-hidden="true" /> {nombreFr(c.like_count)}</span> : null}
                  {c.content_id ? (
                    <Link href={`/communication/instagram/contenus/${c.content_id}`} className="lc-hint">
                      {KIND_COURT[c.content_kind ?? ''] ?? 'Contenu'}
                    </Link>
                  ) : null}
                  {c.missing_since ? <span className="lc-pill lc-pill--idle igc-pill">N’apparaît plus chez Instagram</span> : null}
                </div>
                <p className="igc-texte">{c.text || <em className="lc-hint">…</em>}</p>
              </div>
            </div>
          ))}
        </div>

        {items.length < total ? (
          <button type="button" className="lc-btn mg-plus" onClick={() => setPage((p) => p + 1)}>
            Afficher plus ({items.length} / {total})
          </button>
        ) : null}
      </main>
    </Shell>
  )
}
